import { z } from "zod";

/**
 * Expenses (finances phase). Manual entries plus bank-import batches — see
 * useExpenses.ts for CRUD + the CSV import mutation. Money is integer
 * cents end to end; the frontend never computes a total from these rows,
 * it only formats via centsToUsd (see FinancesPage's ExpensesTab).
 */

export const ExpenseCategorySchema = z.enum([
  "supplies",
  "alcohol_note",
  "equipment",
  "fuel",
  "software",
  "marketing",
  "insurance",
  "licensing",
  "labor",
  "fees",
  "other",
]);
export type ExpenseCategory = z.infer<typeof ExpenseCategorySchema>;

/**
 * `alcohol_note` per the contract, not `alcohol` — the dry-hire model has
 * the client buy alcohol directly (see the shopping-list generator, Phase
 * 4), so this category is for the rare cases Chris fronts/covers stock
 * himself, tracked as a note against the business's own P&L rather than
 * billed through an event. Labeled plainly so that distinction isn't lost
 * in the UI.
 */
export const EXPENSE_CATEGORY_LABELS: Record<ExpenseCategory, string> = {
  supplies: "Supplies",
  alcohol_note: "Alcohol (note)",
  equipment: "Equipment",
  fuel: "Fuel",
  software: "Software",
  marketing: "Marketing",
  insurance: "Insurance",
  licensing: "Licensing",
  labor: "Labor",
  fees: "Fees",
  other: "Other",
};

export const EXPENSE_CATEGORY_BADGE_VARIANT: Record<
  ExpenseCategory,
  "default" | "success" | "warning" | "danger" | "info" | "secondary" | "primary" | "outline"
> = {
  supplies: "info",
  alcohol_note: "warning",
  equipment: "secondary",
  fuel: "default",
  software: "primary",
  marketing: "success",
  insurance: "outline",
  licensing: "outline",
  labor: "danger",
  fees: "default",
  other: "default",
};

export const ExpenseSourceSchema = z.enum(["manual", "bank_import"]);
export type ExpenseSource = z.infer<typeof ExpenseSourceSchema>;

export const ExpenseSchema = z.object({
  id: z.string(),
  date: z.string(),
  amount_cents: z.number(),
  category: ExpenseCategorySchema,
  vendor: z.string().nullable(),
  description: z.string().nullable(),
  is_recurring: z.boolean(),
  source: ExpenseSourceSchema,
  created_at: z.string(),
});
export type Expense = z.infer<typeof ExpenseSchema>;

export const ExpenseListResponseSchema = z.object({
  items: z.array(ExpenseSchema),
  next_cursor: z.string().nullable(),
});
export type ExpenseListResponse = z.infer<typeof ExpenseListResponseSchema>;

// ---- Mutation inputs -----------------------------------------------------

export interface CreateExpenseInput {
  date: string;
  amount_cents: number;
  category: ExpenseCategory;
  vendor?: string;
  description?: string;
}

export interface PatchExpenseInput {
  id: string;
  date?: string;
  amount_cents?: number;
  category?: ExpenseCategory;
  vendor?: string | null;
  description?: string | null;
  is_recurring?: boolean;
}

// ---- CSV import -----------------------------------------------------------

export const ImportExpensesResultSchema = z.object({
  batch_id: z.string(),
  row_count: z.number(),
  imported_count: z.number(),
  skipped_count: z.number(),
  by_category: z.record(z.string(), z.number()),
});
export type ImportExpensesResult = z.infer<typeof ImportExpensesResultSchema>;

export interface ImportExpensesInput {
  csv_text: string;
}
